import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import 'react-vertical-timeline-component/style.min.css';
import styled from 'styled-components';
import SEO from './seo';

// --- Styled Components ---

const TimelineContainer = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #ecf0f1 0%, #f5f6fa 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LoadingIndicator = styled.div`
  width: 100%;
  height: 3px;
  background: linear-gradient(45deg, #3498db, #2ecc71);
  position: fixed;
  top: 0;
  left: 0;
  transform: scaleX(${props => props.progress});
  transform-origin: left;
  transition: transform 0.3s ease;
  z-index: 1000;
`;

// Filter Components
const FilterSection = styled.div`
  margin: 40px auto;
  max-width: 1200px;
  width: 95%;
`;

const FilterHeader = styled.h2`
  text-align: center;
  color: #2c3e50;
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const YearSlider = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding: 15px 5px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  position: relative;
  
  &::-webkit-scrollbar {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #e0e0e0;
    z-index: 1;
  }
`;

const YearButton = styled.button`
  position: relative;
  background: none;
  border: none;
  padding: 10px 20px;
  font-size: 1.1rem;
  color: ${props => props.active ? '#2980b9' : '#95a5a6'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-weight: ${props => props.active ? '600' : '400'};

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.active ? 'linear-gradient(45deg, #3498db, #2ecc71)' : 'transparent'};
    transition: all 0.3s ease;
    z-index: 2;
  }

  &:hover {
    color: #2980b9;
    
    &::after {
      background: ${props => props.active ? 'linear-gradient(45deg, #3498db, #2ecc71)' : '#e0e0e0'};
    }
  }
`;

const FilterInfo = styled.div`
  text-align: center;
  margin-top: 20px;
  color: #7f8c8d;
  font-size: 0.9rem;
  
  span {
    color: #2980b9;
    font-weight: 600;
  }
`;

// Timeline Components
const StyledVerticalTimeline = styled(VerticalTimeline)`
  width: 100% !important;
  max-width: 1200px;
  padding: 0;

  &::before {
    background: linear-gradient(45deg, #3498db, #2ecc71);
    width: 4px;
    left: calc(50% - 2px);
  }

  @media (max-width: 1169px) {
    &::before {
      left: 28px;
    }
  }
`;

const StyledVerticalTimelineElement = styled(VerticalTimelineElement)`
  .vertical-timeline-element-content {
    border-radius: 15px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    background: white;
    padding: 25px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: linear-gradient(45deg, #3498db, #2ecc71);
    }

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    }
  }

  .vertical-timeline-element-date {
    color: #2ecc71;
    font-weight: bold;
    font-size: 1.2em;
    text-shadow: 1px 1px 1px rgba(0,0,0,0.1);
    
    @media (max-width: 1169px) {
      margin-top: 15px;
      color: #2ecc71 !important;
    }
  }

  .vertical-timeline-element-title {
    font-size: 1.6em;
    color: #2980b9;
    margin-bottom: 10px;
    font-weight: 600;
  }

  .vertical-timeline-element-description {
    font-size: 1em;
    color: #7f8c8d;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .vertical-timeline-element-content {
      padding: 20px;
    }
    .vertical-timeline-element-title {
      font-size: 1.4em;
    }
    .vertical-timeline-element-description {
      font-size: 0.9em;
    }
  }
`;

const TimelineIcon = styled.div`
  background: linear-gradient(45deg, #3498db, #2ecc71);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
`;

// Image Components
const ImageContainer = styled.div`
  cursor: pointer;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0; /* A light grey for letterboxing if any */

  /* Shared hover effects */
  &::after {
    content: '🔍';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    font-size: 2rem;
    color: white;
    text-shadow: 0 2px 5px rgba(0,0,0,0.3);
    transition: all 0.3s ease;
    opacity: 0;
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.4);
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 1;
  }

  &:hover::after, &:hover::before {
    opacity: 1;
  }
  
  &:hover .event-image {
    transform: scale(1.05);
  }

  .event-image {
    display: block;
    transition: transform 0.3s ease;
  }

  /* Styles for images within the collage */
  &.collage-item .event-image {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Fill the container, crop if necessary */
    object-position: center; /* Crop from center */
  }

  /* Styles for single images */
  &.single-item .event-image {
    width: 100%;
    height: auto; /* Let the image's aspect ratio determine the height */
  }
`;

const ImageCollage = styled.div`
  display: grid;
  gap: 5px;
  margin-top: 15px;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 4 / 3;

  ${({ imageCount }) => {
    if (imageCount === 2) {
      return `
        grid-template-columns: 1fr 1fr;
      `;
    }
    if (imageCount === 3) {
      return `
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        & > :nth-child(1) {
          grid-area: 1 / 1 / 3 / 2;
        }
        & > :nth-child(2) {
          grid-area: 1 / 2 / 2 / 3;
        }
        & > :nth-child(3) {
          grid-area: 2 / 2 / 3 / 3;
        }
      `;
    }
    if (imageCount >= 4) {
      return `
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
      `;
    }
    return '';
  }}
`;

const MoreImagesIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  transition: background 0.3s ease;
`;

// --- Child Components ---

/**
 * Renders the year filter UI.
 */
const TimelineFilter = ({ years, selectedYear, onSelectYear, eventCount, totalCount }) => (
  <FilterSection>
    <FilterHeader>My Journey Through Time</FilterHeader>
    <YearSlider>
      {years.map(year => (
        <YearButton
          key={year}
          active={selectedYear === year}
          onClick={() => onSelectYear(year)}
        >
          {year === 'All' ? `All Years (${totalCount})` : year}
        </YearButton>
      ))}
    </YearSlider>
    <FilterInfo>
      Showing <span>{eventCount}</span> {eventCount === 1 ? 'memory' : 'memories'}
      {selectedYear !== 'All' && <span> from {selectedYear}</span>}
    </FilterInfo>
  </FilterSection>
);

/**
 * Renders a single event in the timeline.
 */
const TimelineEvent = ({ event, onImageClick }) => {
  const renderImages = () => {
    if (!event.image) return null;

    const images = Array.isArray(event.image) ? event.image : [event.image];

    if (images.length === 1) {
      return (
        <ImageContainer
          className="single-item"
          onClick={() => onImageClick(images[0])}
          style={{ marginTop: '15px', borderRadius: '10px' }}
        >
          <LazyLoadImage
            src={images[0]}
            alt={`${event.title} - Image 1`}
            className="event-image"
            effect="blur"
          />
        </ImageContainer>
      );
    }

    const MAX_IMAGES_DISPLAY = 4;
    const imagesToShow = images.slice(0, MAX_IMAGES_DISPLAY);
    const remainingImages = images.length - imagesToShow.length;

    return (
      <ImageCollage imageCount={imagesToShow.length}>
        {imagesToShow.map((img, index) => (
          <ImageContainer
            key={index}
            className="collage-item"
            onClick={() => onImageClick(img)}
          >
            <LazyLoadImage
              src={img}
              alt={`${event.title} - Image ${index + 1}`}
              className="event-image"
              effect="blur"
            />
            {index === imagesToShow.length - 1 && remainingImages > 0 && (
              <MoreImagesIndicator>
                +{remainingImages}
              </MoreImagesIndicator>
            )}
          </ImageContainer>
        ))}
      </ImageCollage>
    );
  };

  return (
    <StyledVerticalTimelineElement
      date={event.date}
      icon={<TimelineIcon>{event.date.slice(-2)}</TimelineIcon>}
      iconStyle={{ background: 'linear-gradient(45deg, #3498db, #2ecc71)', color: '#fff' }}
    >
      <h3 className="vertical-timeline-element-title">{event.title}</h3>
      <p className="vertical-timeline-element-description">{event.description}</p>
      {renderImages()}
    </StyledVerticalTimelineElement>
  );
};

/**
 * Renders the lightbox for viewing images.
 */
const EventLightbox = ({ lightbox, slides, onClose }) => (
  <Lightbox
    open={lightbox.open}
    close={onClose}
    index={lightbox.index}
    slides={slides}
    render={{
      slide: ({ slide }) => (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <img 
            src={slide.src} 
            alt={slide.alt} 
            style={{ maxHeight: '90vh', maxWidth: '90vw', objectFit: 'contain' }} 
          />
          {slide.title && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '15px',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 5px 0' }}>{slide.title}</h3>
              <p style={{ margin: 0, fontSize: '0.9em' }}>{slide.description}</p>
            </div>
          )}
        </div>
      )
    }}
  />
);

// --- Main Component ---

const Timeline = ({ events }) => {
  const [selectedYear, setSelectedYear] = useState('All');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  // Memoize derived data for performance
  const years = React.useMemo(() => 
    ['All', ...new Set(events.map(event => event.date.split(' ')[1] || event.date))].sort((a, b) => b - a),
    [events]
  );

  const filteredEvents = React.useMemo(() => 
    selectedYear === 'All' 
      ? events 
      : events.filter(event => event.date.includes(selectedYear)),
    [events, selectedYear]
  );

  const slides = React.useMemo(() => 
    filteredEvents
      .flatMap(event => Array.isArray(event.image) ? event.image : (event.image ? [event.image] : []))
      .map(imgSrc => {
        const event = events.find(e => (Array.isArray(e.image) ? e.image.includes(imgSrc) : e.image === imgSrc));
        return {
          src: imgSrc,
          alt: event?.title || 'Timeline Image',
          title: event?.title,
          description: event?.description
        };
      }),
    [filteredEvents, events]
  );

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollProgress(window.scrollY / totalHeight);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleImageClick = (imageUrl) => {
    const imageIndex = slides.findIndex(slide => slide.src === imageUrl);
    if (imageIndex !== -1) {
      setLightbox({ open: true, index: imageIndex });
    }
  };

  return (
    <TimelineContainer>
      <LoadingIndicator progress={scrollProgress} />
      <SEO
        title="Timeline - Nishan Nepali"
        description="Follow the timeline of Nishan Nepali's journey, highlighting significant milestones, projects, and achievements in photography, coding, and academic pursuits."
        keywords="Nishan Nepali, Timeline, Journey, Milestones, Achievements, Photography, Coding, Surkhet, Nepal"
        canonical="https://nishannepali.com.np/timeline"
        image="images/Iskcon_visit_2024.jpg"
      />

      <TimelineFilter
        years={years}
        selectedYear={selectedYear}
        onSelectYear={setSelectedYear}
        eventCount={filteredEvents.length}
        totalCount={events.length}
      />

      <StyledVerticalTimeline layout="2-columns">
        {filteredEvents.map((event, index) => (
          <TimelineEvent 
            key={index} 
            event={event} 
            onImageClick={handleImageClick} 
          />
        ))}
      </StyledVerticalTimeline>

      <EventLightbox
        lightbox={lightbox}
        slides={slides}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </TimelineContainer>
  );
};

export default Timeline;
